import './dashboard.css';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import axios from 'axios';
import DashboardSidebar from '../components/Dashboard/DashboardSidebar';
import DashboardKPICards from '../components/Dashboard/DashboardKPICards';
import DashboardEstadoChart from '../components/Dashboard/DashboardEstadoChart';
import DashboardCalendario from '../components/Dashboard/DashboardCalendario';
import DashboardVencimientos from '../components/Dashboard/DashboardVencimientos';
import DashboardMonitoreo from '../components/Dashboard/DashboardMonitoreo';
import DashboardQuienArmo from '../components/Dashboard/DashboardQuienArmo';

const EMPLEADOS_CONTRACTUALES = [
  'JuanG', 'LucaP', 'DanielaM', 'DiegoD', 'DiegoS',
  'OrelyC', 'StellaM', 'LucianaF', 'CandelaS', 'MagaliG',
  'PilarS', 'Augusto', 'PaulaC', 'MariaE', 'rosario',
];


export default function Dashboard() {
  const [cookies] = useCookies(['user']);
  const [stros, setStros] = useState([]);
  const [estados, setEstados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmpleado, setSelectedEmpleado] = useState(null);
  const usuario = cookies.user?.usuario;
  const isStellaM = usuario === 'StellaM' || usuario === 'DiegoS';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const estadosRes = await axios.get('https://back.agsseguros.online/api/estados/estadosAll');
        setEstados(estadosRes.data);

        let allAssignedStros = [];
        if (isStellaM) {
          // StellaM monitors all contractuales employees
          const res = await axios.get('https://back.agsseguros.online/api/tabla2026/buscarConFiltros', { params: { asignado: EMPLEADOS_CONTRACTUALES } });
          allAssignedStros = res.data || [];
        } else {
          // Regular employee only needs their own assigned cases (all-time history)
          const res = await axios.get('https://back.agsseguros.online/api/tabla2026/buscarConFiltros', { params: { asignado: [usuario] } });
          allAssignedStros = res.data || [];
        }

        // Deduplicate claims by id
        const uniqueStros = [];
        const seenIds = new Set();
        for (const s of allAssignedStros) {
          if (s && s.id && !seenIds.has(s.id)) {
            seenIds.add(s.id);
            uniqueStros.push(s);
          }
        }

        setStros(uniqueStros);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    if (cookies.user) {
      fetchData();
    }
  }, [cookies.user, isStellaM, usuario]);

  // Build quick stats per employee for the selector chips
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const in7Days = new Date(today);
  in7Days.setDate(in7Days.getDate() + 7);
  const in7DaysStr = in7Days.toISOString().split('T')[0];

  const empleadoChips = useMemo(() => {
    if (!isStellaM) return [];
    return EMPLEADOS_CONTRACTUALES.map(emp => {
      const empStros = stros.filter(s => s.empleadoAsignado === emp);
      const total = empStros.length;
      const vencidos = empStros.filter(s => s.fechaVencimiento && s.fechaVencimiento < todayStr).length;
      return { nombre: emp, total, vencidos };
    }).filter(e => e.total > 0)
      .sort((a, b) => b.vencidos - a.vencidos || b.total - a.total);
  }, [isStellaM, stros, todayStr]);

  // Determine which stros to show
  const viewingEmpleado = selectedEmpleado || usuario;
  const strosUsuario = stros.filter(s => s.empleadoAsignado === viewingEmpleado);

  const totalAsignados = strosUsuario.length;
  const vencenHoy = strosUsuario.filter(s => s.fechaVencimiento === todayStr).length;
  const vencen7Dias = strosUsuario.filter(s => {
    if (!s.fechaVencimiento) return false;
    return s.fechaVencimiento > todayStr && s.fechaVencimiento <= in7DaysStr;
  }).length;
  const vencidos = strosUsuario.filter(s => {
    if (!s.fechaVencimiento) return false;
    return s.fechaVencimiento < todayStr;
  }).length;

  const formatToday = () => {
    return today.toLocaleDateString('es-AR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const handleSelectEmpleado = (empName) => {
    if (empName === selectedEmpleado) {
      setSelectedEmpleado(null);
    } else {
      setSelectedEmpleado(empName);
    }
  };

  if (loading) {
    return (
      <div className='dashboard-loading'>
        <div className='dashboard-loading-spinner'></div>
        <p>Cargando dashboard...</p>
      </div>
    );
  }

  return (
    <div className='dashboard-layout'>
      <DashboardSidebar user={cookies.user} />

      <main className='dashboard-main'>
        <div className='dashboard-header'>
          <div className='dashboard-header-left'>
            <h1>Dashboard</h1>
            <p>
              {selectedEmpleado
                ? <>Viendo siniestros de <strong>{selectedEmpleado}</strong></>
                : `Bienvenido de vuelta, ${usuario}. Aquí está tu resumen del día.`}
            </p>
          </div>
          <div className='dashboard-header-right'>
            <div className='dashboard-date-badge'>
              <i className='bi bi-calendar3'></i>
              {formatToday()}
            </div>
          </div>
        </div>

        {/* Employee selector strip - only for StellaM */}
        {isStellaM && (
          <div className='emp-selector-container'>
            <div className='emp-selector-label'>
              <i className='bi bi-people-fill'></i>
              Equipo Contractuales
            </div>
            <div className='emp-selector-strip'>
              {/* "Mis datos" chip */}
              <button
                className={`emp-chip ${!selectedEmpleado ? 'emp-chip--active' : ''}`}
                onClick={() => setSelectedEmpleado(null)}
              >
                <div className='emp-chip-avatar emp-chip-avatar--self'>
                  <i className='bi bi-person-fill'></i>
                </div>
                <div className='emp-chip-info'>
                  <span className='emp-chip-name'>Mis datos</span>
                </div>
              </button>

              <div className='emp-selector-divider'></div>

              {/* Employee chips */}
              {empleadoChips.map(emp => (
                <button
                  key={emp.nombre}
                  className={`emp-chip ${selectedEmpleado === emp.nombre ? 'emp-chip--active' : ''}`}
                  onClick={() => handleSelectEmpleado(emp.nombre)}
                >
                  <div className='emp-chip-avatar'>
                    {emp.nombre.substring(0, 2).toUpperCase()}
                  </div>
                  <div className='emp-chip-info'>
                    <span className='emp-chip-name'>{emp.nombre}</span>
                    <span className='emp-chip-stats'>
                      {emp.total} casos
                      {emp.vencidos > 0 && (
                        <span className='emp-chip-vencidos'>{emp.vencidos} venc.</span>
                      )}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <DashboardKPICards
          totalAsignados={totalAsignados}
          vencenHoy={vencenHoy}
          vencen7Dias={vencen7Dias}
          vencidos={vencidos}
        />

        <div className='dashboard-content-grid'>
          <DashboardEstadoChart stros={strosUsuario} estados={estados} />
          <DashboardCalendario stros={strosUsuario} />
        </div>

        <div className='dashboard-second-row'>
          <DashboardVencimientos stros={strosUsuario} />
        </div>

        {/* Carpetas armadas por (QUIEN_LO_ARMO de la planilla de reclamo).
            El supervisor sin empleado seleccionado ve el equipo completo. */}
        <div className='dashboard-second-row' style={{ marginTop: '28px' }}>
          <DashboardQuienArmo
            stros={isStellaM && !selectedEmpleado ? stros : strosUsuario}
            scopeLabel={isStellaM && !selectedEmpleado ? 'Equipo Contractuales completo' : `Siniestros asignados a ${viewingEmpleado}`}
          />
        </div>

        {/* Monitoring table - only for StellaM */}
        {isStellaM && (
          <div className='dashboard-second-row' style={{ marginTop: '28px' }}>
            <DashboardMonitoreo
              stros={stros}
              estados={estados}
              onSelectEmpleado={handleSelectEmpleado}
              selectedEmpleado={selectedEmpleado}
            />
          </div>
        )}
      </main>
    </div>
  );
}
